import { useEffect, useMemo, useState } from 'react';

import { useModalForm } from '../../../../foundation/hooks';
import {
  useGetVariantAssessmentArgumentsQuery,
  useUpdateVariantAssessmentArgumentsMutation,
} from '../../../api/variantApi';
import { VARIANT_STAGE_TYPES } from '../../constants';
import {
  checkIsCnvVariant,
  filterArgumentsByStage,
  StageFilterOptions,
} from '../../utils';
import {
  handleArgumentSelection,
  useArgumentsTableColumns,
} from '../helpers/argumentsHelpers';
import { StructuredArgument } from '../helpers/structureArgumentsData';

interface UseStageArgumentsModalLogicProps {
  closeModal: () => void;
  stage: string;
  stageLabel: string;
  dataSource: StructuredArgument[];
  variantId: string;
  variant_consequence: string;
  variant_type?: string;
  variant_transcript_consequences?: string[];
}

/**
 * Custom hook that encapsulates all the business logic for StageArgumentsModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 *
 * Per the RTK Query tutorial:
 * "You should normally use the query hooks to access cached data in components -
 * you shouldn't write your own useSelector calls to access fetched data."
 * If you need the same data in multiple components, just call the same query hook
 * with the same arguments in each component! RTK Query will make sure the data
 * is only fetched once, and each component will re-render as needed.
 */
export const useStageArgumentsModalLogic = ({
  closeModal,
  stage,
  stageLabel,
  dataSource,
  variantId,
  variant_consequence,
  variant_type,
  variant_transcript_consequences,
}: UseStageArgumentsModalLogicProps) => {
  const [updateVariantAssessmentArgumentsMutation] =
    useUpdateVariantAssessmentArgumentsMutation();

  // Calling the query hook automatically fetches and caches the data; the
  // result object exposes data plus isLoading/isSuccess/isError flags
  const { data: argumentsForClassification } =
    useGetVariantAssessmentArgumentsQuery(
      { variant_id: variantId },
      { skip: !variantId },
    );

  const {
    isLoading: isSaving,
    setIsLoading: setIsSaving,
    handleModalClose,
    createModalOpts,
  } = useModalForm({
    onClose: closeModal,
    successMessage: 'Arguments updated successfully',
    stageType: VARIANT_STAGE_TYPES.INTERPRETATION,
  });

  const [selectedArgument, setSelectedArgument] =
    useState<StructuredArgument | null>(null);

  const [internalSource, setInternalSource] =
    useState<StructuredArgument[]>(dataSource);

  const [isArgumentModalVisible, setArgumentModalVisibility] = useState(false);

  // createModalOpts comes from useModalForm (global) – no local override needed.

  useEffect(() => {
    setInternalSource(dataSource);
  }, [dataSource]);

  const handleArgumentEditClick = (record: StructuredArgument) => {
    setSelectedArgument(record);
    setArgumentModalVisibility(true);
  };

  const columns = useArgumentsTableColumns(
    true,
    false,
    (record) => handleArgumentSelection(record, setInternalSource),
    handleArgumentEditClick,
    true,
  );

  // Determine CNV context flag for filtering.
  const isCnvVariant = useMemo(() => {
    return checkIsCnvVariant(
      variant_type,
      variant_consequence,
      variant_transcript_consequences,
    );
  }, [variant_type, variant_consequence, variant_transcript_consequences]);

  const hasChanges = useMemo(() => {
    // Filter arguments by current stage type and
    // for molecular impact stage additionally by variant_consequence

    const stageData = filterArgumentsByStage(
      argumentsForClassification ?? [],
      stage,
      variant_consequence,
      {
        requireConsequenceForMolecularImpact: true,
        isCnvVariant,
      } as StageFilterOptions,
    );

    // Filter out stage rows from dataSource
    const filteredDataSource = internalSource.filter(
      (item) => !item.isStageRow,
    );

    // If lengths are different after filtering, there are changes
    if (stageData.length !== filteredDataSource.length) {
      return true;
    }

    // Compare each item's selected and comment fields by matching ids
    return stageData.some((item) => {
      const dataSourceItem = filteredDataSource.find(
        (dsItem) => dsItem.id === item.id,
      );
      if (!dataSourceItem) {
        return true; // If item not found in dataSource, there's a change
      }

      return (
        item.selected !== dataSourceItem.selected ||
        item.comment !== dataSourceItem.comment ||
        (item.is_argument_met ?? true) !==
          (dataSourceItem.is_argument_met ?? true)
      );
    });
  }, [
    internalSource,
    argumentsForClassification,
    stage,
    variant_consequence,
    isCnvVariant,
  ]);

  const handleSaveChanges = async () => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);
      const argumentsToSave = internalSource.filter((arg) => {
        if (arg.isStageRow) {
          return false;
        }
        const hasComment =
          arg.comment && arg.comment.trim() !== '' && arg.comment !== '<p></p>';
        return arg.selected || hasComment;
      });

      const requestData = {
        argumentdata: argumentsToSave.map((arg) => ({
          argument_id: arg.id as number,
          comment: arg.comment as string,
          is_argument_met: arg.is_argument_met ?? true,
        })),
        stage: stage,
      };

      await updateVariantAssessmentArgumentsMutation({
        variant_id: variantId,
        requestData,
      }).unwrap();

      // invalidatesTags marks VariantArguments + VariantStages + InterpretationZone
      // stale, so those queries refetch automatically

      handleModalClose();
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const closeArgumentModal = () => {
    setArgumentModalVisibility(false);
  };

  return {
    isSaving,
    selectedArgument,
    internalSource,
    setInternalSource,
    isArgumentModalVisible,
    stageLabel,
    createModalOpts,
    columns,
    hasChanges,
    handleSaveChanges,
    closeArgumentModal,
  };
};

export default useStageArgumentsModalLogic;
